// Check if an activity is full
export const isActivityFull = (activity) => {
  if (!activity) return true;
  return activity.currentParticipants >= activity.maxParticipants;
};

// Get the number of available spots
export const getAvailableSpots = (activity) => {
  if (!activity) return 0;
  return activity.maxParticipants - activity.currentParticipants;
};

export const INDIVIDUAL_CATEGORIES = ['Shiatsu', 'Chi Nei Tsang', 'Soin'];

const formatClock = (date) =>
  new Date(date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

// Helper function to check if an activity is an individual session
export const isIndividualSession = (category, typeConfig) => {
  if (typeConfig?.bookingMode === 'individual' || typeConfig?.bookingMode === 'individual_with_treatment') {
    return true;
  }
  return INDIVIDUAL_CATEGORIES.includes(category);
};

/**
 * Highest headcount this user may set on an existing booking.
 * Room left in the session, capped by the per-booking maximum (1 individual, else ≤ 4).
 */
export const getModifyParticipantsLimit = ({
  category,
  typeConfig,
  maxParticipants,
  currentRegistrations,
  currentParticipants,
  maxPerBooking,
} = {}) => {
  const own = Number(currentParticipants) || 0;
  const capacity = Number(maxParticipants) || 0;
  const taken = Number(currentRegistrations) || 0;
  const perBooking = isIndividualSession(category, typeConfig)
    ? 1
    : Math.min(4, Number(maxPerBooking) || Number(typeConfig?.maxParticipantsPerBooking) || 4);
  const roomForThisUser = Math.max(own, capacity - taken + own);
  return Math.max(1, Math.min(perBooking, roomForThisUser));
};

/**
 * Same event title on every calendar. /admin/activities is the reference:
 * internal category name, individual = start + 0/1|1/1, group = range + n/max|Complet.
 */
export const buildCalendarEvent = (activity, { displayNames = {}, typesByName = {}, t } = {}) => {
  const category = activity.category;
  const typeConfig = typesByName[category];
  const individual = isIndividualSession(category, typeConfig);
  const start = new Date(activity.date);
  const durationMin = parseInt(activity.duration, 10) || 60;
  const end = new Date(start.getTime() + durationMin * 60000);
  const current = activity.currentParticipants || 0;
  const max = activity.maxParticipants || 1;
  const full = current >= max;
  const label = category;
  const availabilityText = individual
    ? (full ? '1/1' : '0/1')
    : (full ? (t ? t('noAvailableSpots') : 'Complet') : `${current}/${max}`);
  const timeDisplay = individual
    ? formatClock(start)
    : `${formatClock(start)} - ${formatClock(end)}`;

  return {
    id: activity._id,
    slug: activity.slug,
    title: `${label}\n${timeDisplay}\n${availabilityText}`,
    start,
    end,
    category,
    allDay: false,
    activity,
  };
};

// Get the spots label for display
export const getSpotsLabel = (activity) => {
  if (!activity) return '';
  
  const spotsLeft = activity.maxParticipants - activity.currentParticipants;
  
  if (isIndividualSession(activity.category)) {
    return spotsLeft === 0 ? 'complet' : 'disponible';
  }
  
  return spotsLeft === 0 ? 'complet' : `${spotsLeft} place${spotsLeft > 1 ? 's' : ''} disponible${spotsLeft > 1 ? 's' : ''}`;
};

// Get the participants text for display
export const getParticipantsText = (activity) => {
  if (!activity) return '';
  
  if (isIndividualSession(activity.category)) {
    return activity.currentParticipants === 0 ? 'disponible' : 'réservé';
  }
  
  return `${activity.currentParticipants}/${activity.maxParticipants}`;
}; 